import { Box, Flex, Icon, Tooltip } from "@chakra-ui/react";
import { InfoOutlineIcon } from "@chakra-ui/icons";
import { FieldPathByValue, UseFormReturn, useWatch } from "react-hook-form";
import { FloatingField } from "design-system";
import SwitchField from "components/FormElements/Fields/SwitchField";
import { useEffect, useMemo } from "react";
import { TRIGGER_DEFAULT_VALUES } from "definitions/constants/trigger";
import { Maybe } from "yup";
import { SharedTemplateSchema } from "features/Resources/SharedTemplate/schema";
import { isImmediateNoOffsetTrigger } from "features/Trigger/helpers";
import { schedulingFields } from "./fields";
import { TimingDataSchema, TriggerSchema } from "./schema";

export default function TimeOfDay<Schema extends SharedTemplateSchema>({
  fieldName,
  allowWeekendsFieldName,
  form: {
    setValue,
    control,
    formState: { errors },
  },
}: {
  fieldName: FieldPathByValue<Schema, Maybe<TriggerSchema>>;
  allowWeekendsFieldName?: string;
  form: UseFormReturn<Schema>;
}) {
  const fields = schedulingFields(fieldName);
  // using "useWatch" - "watch" was not triggering the useEffect to run when referenceEvent or offsetDirection changed
  const [timeOfDay, timezoneType, referenceEvent, offsetDirection] = useWatch({
    control,
    name: [
      fields.timeOfDay.name as FieldPathByValue<Schema, TimingDataSchema["timeOfDay"]>,
      fields.timezoneType.name as FieldPathByValue<Schema, TimingDataSchema["timezoneType"]>,
      fields.referenceEvent.name as FieldPathByValue<Schema, TimingDataSchema["referenceEvent"]>,
      fields.offsetDirection.name as FieldPathByValue<Schema, TimingDataSchema["offsetDirection"]>,
    ],
  });

  const isImmediateTrigger = useMemo(
    () => isImmediateNoOffsetTrigger({ referenceEvent, offsetDirection }),
    [referenceEvent, offsetDirection],
  );

  useEffect(() => {
    // clear time related fields if the trigger is immediate
    // NOTE this is not idea (we'd instead do this on submit, as this loses original value if immediate is toggled)
    if (isImmediateTrigger) {
      setValue(fields.timeOfDay.name, undefined);
      setValue(fields.timezoneType.name, undefined);
      setValue(fields.specificTimezone.name, undefined);
    }
  }, [setValue, isImmediateTrigger, fields.timeOfDay.name, fields.timezoneType.name, fields.specificTimezone.name]);

  useEffect(() => {
    if (!timeOfDay && !isImmediateTrigger) {
      setValue(fields.timeOfDay.name, TRIGGER_DEFAULT_VALUES.trigger.data.timeOfDay);
    }
  }, [setValue, timeOfDay, isImmediateTrigger, fields.timeOfDay.name]);

  useEffect(() => {
    if (!timezoneType && !isImmediateTrigger) {
      setValue(fields.timezoneType.name, TRIGGER_DEFAULT_VALUES.trigger.data.timezoneType);
    }
  }, [setValue, timezoneType, isImmediateTrigger, fields.timezoneType.name]);

  // Hide the time field as its not relevant
  if (isImmediateTrigger) {
    return null;
  }

  return (
    <Flex gap="2" align="center" flexWrap="wrap">
      <Box width="auto">
        <FloatingField
          name={fields.timeOfDay.name}
          errors={errors[fields.timeOfDay.name]?.message as string | undefined}
        >
          <FloatingField.Label>{fields.timeOfDay.label}</FloatingField.Label>
          <FloatingField.TimePicker control={control} name={fields.timeOfDay.name} />
        </FloatingField>
      </Box>
      <Box minWidth="228px" width="228px">
        <FloatingField
          name={fields.timezoneType.name}
          errors={errors[fields.timezoneType.name]?.message as string | undefined}
        >
          <FloatingField.Label>{fields.timezoneType.label}</FloatingField.Label>
          <FloatingField.StaticSelect
            control={control}
            name={fields.timezoneType.name}
            options={fields.timezoneType.options}
            isClearable={false}
          />
        </FloatingField>
      </Box>
      {timezoneType === "specific_timezone" && (
        <Box minWidth="300px" width="300px">
          <FloatingField
            name={fields.specificTimezone.name}
            errors={errors[fields.specificTimezone.name]?.message as string | undefined}
          >
            <FloatingField.Label>{fields.specificTimezone.label}</FloatingField.Label>
            <FloatingField.Timezone control={control} name={fields.specificTimezone.name} />
          </FloatingField>
        </Box>
      )}
      {allowWeekendsFieldName && (
        <SwitchField
          name={allowWeekendsFieldName}
          label={
            <Flex align="center" gap={2}>
              Allow weekend scheduling
              <Tooltip
                label={
                  "When disabled, weekend dates are moved to weekdays. " +
                  "Dates scheduled before an event move to the Friday before the weekend. " +
                  "Dates scheduled after an event move to the Monday after the weekend."
                }
                fontSize="sm"
                placement="top"
                hasArrow
              >
                <Icon as={InfoOutlineIcon} boxSize={3.5} color="gray.500" cursor="help" />
              </Tooltip>
            </Flex>
          }
          control={control}
          errors={errors}
          formFieldProps={{ width: "auto", pt: 2 }}
        />
      )}
    </Flex>
  );
}
